import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  authApi,
  clearAuth,
  getStoredAuth,
  setUnauthorizedHandler,
  storeAuth,
} from '../services/api';
import { Routes } from '../routes';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const history = useHistory();
  const stored = getStoredAuth();
  const [tenantId, setTenantId] = useState(stored.tenantId);
  const [tenantName, setTenantName] = useState(stored.tenantName);
  const [clientSecret, setClientSecret] = useState(stored.clientSecret);
  const [accessToken, setAccessToken] = useState(stored.accessToken);

  const isAuthenticated = Boolean(accessToken);

  const logout = useCallback(() => {
    clearAuth();
    setTenantId('');
    setTenantName('');
    setClientSecret('');
    setAccessToken('');
    history.push(Routes.Login.path);
  }, [history]);

  // Any authenticated API 401 clears the session and sends the user to login.
  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const register = async (name) => {
    const { data } = await authApi.register(name);
    setTenantId(data.tenantId);
    setTenantName(data.name);
    setClientSecret(data.clientSecret);
    storeAuth({
      tenantId: data.tenantId,
      tenantName: data.name,
      clientSecret: data.clientSecret,
      accessToken: '',
    });
    return data;
  };

  const login = async (tid, secret) => {
    const { data } = await authApi.login(tid, secret);
    setTenantId(tid);
    setClientSecret(secret);
    setAccessToken(data.accessToken);
    storeAuth({ tenantId: tid, clientSecret: secret, accessToken: data.accessToken });

    try {
      const { data: profile } = await authApi.getProfile();
      setTenantName(profile.name);
      storeAuth({ tenantName: profile.name });
    } catch {
      // Profile fetch is best-effort; login still succeeds.
    }

    return data;
  };

  const refreshProfile = async () => {
    const { data } = await authApi.getProfile();
    setTenantName(data.name);
    storeAuth({ tenantName: data.name });
    return data;
  };

  const updateTenantName = async (name) => {
    const { data } = await authApi.updateProfile(name);
    setTenantName(data.name);
    storeAuth({ tenantName: data.name });
    return data;
  };

  const value = useMemo(
    () => ({
      tenantId,
      tenantName,
      clientSecret,
      accessToken,
      isAuthenticated,
      register,
      login,
      refreshProfile,
      updateTenantName,
      logout,
    }),
    [tenantId, tenantName, clientSecret, accessToken, isAuthenticated, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
